import { IBaseFormElementProps, BaseFormElement } from "../BaseFormElement";
import { CountryCode } from "../../../Wrap/Constants/Enums/Domain/CountryCode";
import { Address } from "../../../Wrap/Models/Domains/Address/Address";
import * as React from "react";
import * as $ from "jquery";
import { FindAddressesClient } from "../../../Wrap/Api/Address/FindAddresses";
import { RetrieveAddressDetailsClient } from "../../../Wrap/Api/Address/RetrieveAddressDetails";
import { AddressSearchResults } from "../../../Wrap/Models/Domains/Address/AddressSearchResults";
import { AddressDetailsSearchResults } from "../../../Wrap/Models/Domains/Address/AddressDetailsSearchResults";
import { PostCodeAnywhereAddressDetails } from "../../../Wrap/Models/Domains/Address/PostCodeAnywhereAddressDetails";
import { PostCodeAnywhereAddress } from "../../../Wrap/Models/Domains/Address/PostCodeAnywhereAddress";
import "jquery-ui/ui/widgets/autocomplete";
import { InstructionAddress } from "../../../Wrap/Models/Models/Instructions/InstructionAddress";
import { AddressHelper } from "../../../applications/Shared/Common/AddressHelper";
import { CommonHelper } from "../../../react-core/Helpers/CommonHelper";

export class AutocompleteResult {
    value: string;
    label: string;
    data: any;
}

export interface IPostCodeAnywhereSearchSpecificProps extends IBaseFormElementProps {
    id?: string;

    placeholder?: string;

    countryCode: CountryCode;

    onAddressSelected: (address: Address) => void;

    onLookupError: () => void;

    automationId?: string;

    disabled?: boolean;

    subLabelId?: string;

    useLabelForAddress?: boolean;
}

export interface IPostCodeAnywhereSearchProps
    extends IPostCodeAnywhereSearchSpecificProps,
        React.Props<PostCodeAnywhereSearch> {}

export class PostCodeAnywhereSearch extends BaseFormElement<IPostCodeAnywhereSearchProps, any> {
    private _pcaInputRef: HTMLInputElement;

    private _lastId: string;

    private _findAddressClient: FindAddressesClient;

    private _retrieveAddressDetailsClient: RetrieveAddressDetailsClient;

    constructor(props: IPostCodeAnywhereSearchProps) {
        super(props);
        this.state = {};
        this._findAddressClient = new FindAddressesClient();
        this._retrieveAddressDetailsClient = new RetrieveAddressDetailsClient();
    }

    public setDisable() {
        this.setState({ disable: true });
    }

    public isValid(): boolean {
        return true;
    }

    public componentDidMount() {
        const $inputRef = $(this._pcaInputRef);

        const autocomplete = ($inputRef as any)
            .autocomplete({
                source: (request, responseFn) => {
                    this._findAddressClient
                        .get(this.getPcaSearchValue(), this.props.countryCode, this.getLastId())
                        .onSuccess((results: AddressSearchResults) => {
                            if (results.Succeeded) {
                                this._lastId = null;
                                const mappedSearchResults = this.transformSearchResults(results.Results);
                                responseFn(mappedSearchResults);
                            } else {
                                this.props.onLookupError();
                            }
                        });
                },
                minLength: 3,
            })
            .on("autocompleteselect", (event: any, ui: any) => {
                this.handleSearchResultSelection(ui.item);
            });
    }

    public render(): JSX.Element {
        return (
            <div className="ui-front">
                <input
                    type="search"
                    role="combobox"
                    id={this.props.id}
                    className="form-input"
                    placeholder={this.props.placeholder || "Start typing address or postcode..."}
                    ref={(pcaInput: HTMLInputElement) => (this._pcaInputRef = pcaInput)}
                    data-automation-id={this.props.automationId}
                    disabled={this.state.disable == true ? true : this.props.disabled}
                    aria-describedby={this.props.subLabelId}
                />
            </div>
        );
    }

    private handleSearchResultSelection(selectedResult: AutocompleteResult) {
        if (!selectedResult.data) {
            return;
        }

        const nextOperation: string = selectedResult.data.Next;
        if (!nextOperation) {
            throw new Error("Invalid PCA response. (Next operation not specified)");
        }
        switch (nextOperation) {
            case "Retrieve":
                this.handleFinalSearchResultSelection(selectedResult);
                break;
            case "Find":
                this.handleDetailedSearchResultSelection(selectedResult);
                break;
            default:
                throw new Error("Invalid PCA response. (unrecognized Next operation)");
        }
    }

    private handleDetailedSearchResultSelection(searchResult: AutocompleteResult): void {
        if (!searchResult.data || !searchResult.data.Next || searchResult.data.Next !== "Find") {
            throw new Error("Invalid operation.");
        }

        this._lastId = searchResult.data.Id;

        setTimeout(() => {
            ($(this._pcaInputRef) as any).autocomplete("search");
        }, 1);
    }

    private handleFinalSearchResultSelection(searchResult: AutocompleteResult): void {
        if (
            !searchResult.data ||
            !searchResult.data.Next ||
            searchResult.data.Next.toLowerCase() !== "Retrieve".toLowerCase()
        ) {
            throw new Error("Invalid operation.");
        }

        this._pcaInputRef.value = "";
        this.retrieveDetailsForAddress(searchResult.data.Id);
    }

    private retrieveDetailsForAddress(addressId: string): void {
        this._retrieveAddressDetailsClient.get(addressId).onSuccess((data: AddressDetailsSearchResults) => {
            if (data.Succeeded) {
                if (data.Results.length > 0) {
                    this.handleFinalAddressSelection(data.Results[0]);
                }
            } else {
                this.props.onLookupError();
            }
        });
    }

    private handleFinalAddressSelection(pcaAddtessDetails: PostCodeAnywhereAddressDetails) {
        const address = this.props.useLabelForAddress
            ? this.mapAddressFromPcaLabel(pcaAddtessDetails)
            : this.mapAddressFromPcaResult(pcaAddtessDetails);
        this.props.onAddressSelected(address);
        this._pcaInputRef.value = "";
    }

    private getPcaSearchValue(): string {
        return this._pcaInputRef.value;
    }

    private getLastId() {
        return this._lastId;
    }

    private mapAddressFromPcaLabel(pcaResult: PostCodeAnywhereAddressDetails): Address {
        const address = this.mapAddressFromPcaResult(pcaResult);
        const label = pcaResult.Label;
        if (label) {
            let addresses = label.split(/[\n]/);
            addresses = addresses.filter(
                (x) =>
                    x.toUpperCase() != pcaResult.CountryName?.toUpperCase() &&
                    x.toUpperCase() != pcaResult.PostalCode?.toUpperCase()
            );
            if (pcaResult.CountryName == pcaResult.City) {
                const addressLines = [addresses[2], addresses[3], addresses[4], addresses[5]];
                const notNullAddressLines = addressLines.filter((x) => !CommonHelper.isNullOrEmpty(x));
                const joinedAddress = notNullAddressLines.join(", ") + ", " + pcaResult.City;
                const MAXCHARNO = 35;
                if (joinedAddress.length <= MAXCHARNO) {
                    addresses.push(pcaResult.City);
                }
            }

            //const postCodeIndex = addresses.findIndex((ele) => ele == pcaResult.PostalCode);
            address.Line1 = addresses[0];
            address.Line2 = addresses[1];
            address.Line3 = addresses[2];
            address.Line4 = addresses[3];
            address.Line5 = addresses[4];
            address.Line6 = addresses[5];
        }
        return this.formatAddress(address, pcaResult);
    }

    private mapAddressFromPcaResult(pcaResult: PostCodeAnywhereAddressDetails): Address {
        const address = {} as Address;
        address.Line1 = pcaResult.Line1;
        address.Line2 =
            pcaResult.Line2 ||
            (pcaResult.Line1.toLowerCase().includes(pcaResult.Street.toLowerCase()) ? "" : pcaResult.Street);
        address.Line3 = pcaResult.Line3;
        address.Line4 = pcaResult.City;
        address.Line5 = this.getProvinceIfDifferentToCity(pcaResult.Province, pcaResult.City);
        address.Postcode = pcaResult.PostalCode;
        address.CountryCode = this.props.countryCode; // not returned from PCA

        return this.formatAddress(address, pcaResult);
    }

    private formatAddress(address: Address, pcaResult: PostCodeAnywhereAddressDetails): Address {
        address.Line1 = AddressHelper.GetFormattedAddressLine1(address);
        address.Line2 = AddressHelper.GetFormattedAddressLine2(address);
        address.Line3 = AddressHelper.GetFormattedAddressLine3(address, pcaResult);
        return address;
    }
    private getProvinceIfDifferentToCity(province?: string, city?: string): string {
        if (!province || !city) {
            return province;
        }

        return province.lastIndexOf(city, 0) >= 0 ? null : province;
    }

    private transformSearchResults(items: PostCodeAnywhereAddress[]): Array<AutocompleteResult> {
        const results = new Array<AutocompleteResult>();

        if (!items.length) {
            const emptyResult: AutocompleteResult = {
                label: "No results found",
                value: null,
                data: null,
            };
            results.push(emptyResult);
            return results;
        }

        for (let i = 0; i < items.length; i++) {
            const item = new AutocompleteResult();
            item.data = items[i];

            if (items[i].Text && items[i].Description) {
                item.label = items[i].Text + " - " + items[i].Description;
            } else if (items[i].Text) {
                item.label = items[i].Text;
            } else if (items[i].Description) {
                item.label = items[i].Description;
            }

            results.push(item);
        }

        return results;
    }
}
